import React, { useCallback, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FirstDayOfWeek } from "../../../lib/datetime";
import { useI18n } from "../../../lib/i18n/i18n_provider";
import { Icon } from "../../../components";
import {
  formatDayRange,
  formatMeetingRoomDateRange,
} from "../../../offering/format_date_range";
import { parseTime, Time, TimeInterval } from "../../../offering/time_utils";
import { useAnalytics } from "../../../lib/analytics/analytics";
import { BookingType, PricingType, Space } from "../../../data";
import {
  DateTimePicker,
  DailyValue,
  HourlyValue,
  DateTimeValue,
  isHourlyValue,
} from "./date_time_picker";
import { endOfMonth, parseISO, startOfMonth } from "date-fns";
import {
  useDurationPickerFeatureFlag,
  useMeetingRoomDailyPricingFeatureFlag,
} from "../../../core/splitio";
import { AvailableDay, useGetSpaceAvailableDays } from "../../../offering/api";
import classNames from "classnames";
import { PickingRange } from "../date_range_picker/date_range_picker";
import { toDay } from "@/offering/day_utils";
import { useMediaQuery } from "../../../hooks";

export interface TimeSlot {
  startTime: string;
  endTime: string[];
  hasBetterRate?: boolean;
  hasHigherRate?: boolean;
  price?: number;
}

interface CheckoutDateTimePickerProps<T> {
  value?: T;
  onComplete: (value?: DateTimeValue) => void;
  onNextVisibleDates: (startDate: Date, endDate: Date) => void;
  isOutsideRange: (date: Date) => boolean;
  isBlockedDate: (date: Date) => boolean;
  onSelectDate: (date: Date) => void;
  firstDayOfWeek?: FirstDayOfWeek;
  timeslots: TimeSlot[];
  disabled?: boolean;
  placeholder?: string;
  availableDays?: AvailableDay[];
  space?: Space | null;
  pickingRange: PickingRange;
  onSave?: () => void;
}

export function CheckoutDateTimePicker<T extends DateTimeValue>(
  props: CheckoutDateTimePickerProps<T>
) {
  const {
    value,
    onComplete,
    onNextVisibleDates,
    onSelectDate,
    timeslots,
    disabled,
    placeholder,
    availableDays,
    space,
    pickingRange,
    onSave,
  } = props;

  const { device } = useMediaQuery();

  const meetingRoomDailPricingFeatureFlag =
    useMeetingRoomDailyPricingFeatureFlag(space?.partnerID || "");
  const durationPickerFeatureFlag = useDurationPickerFeatureFlag();

  const { fbt, locale } = useI18n();
  const analytics = useAnalytics();

  let hasDailyPrice = false;
  let hasHourlyPrice = false;
  space?.pricings.forEach((p) => {
    if (p.type === PricingType.Daily) {
      hasDailyPrice = true;
    } else if (p.type === PricingType.Hourly) {
      hasHourlyPrice = true;
    }
  });

  const [show, setShow] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<DateTimeValue>();
  const [switchValue, setSwitchValue] = useState(
    isHourlyValue(value) && hasHourlyPrice
      ? BookingType.Hourly
      : BookingType.Daily
  );

  const {
    data: { availableDays: localAvailableDates },
  } = useGetSpaceAvailableDays(
    space?.id,
    !isHourlyValue(localValue) && localValue?.startDate && localValue.endDate
      ? {
          startDate: toDay(startOfMonth(parseISO(localValue.startDate))),
          endDate: toDay(endOfMonth(parseISO(localValue.endDate))),
        }
      : undefined
  );

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleHourlyChange = useCallback(
    (value: HourlyValue) => {
      if (value.date) {
        onSelectDate(parseISO(value.date));
        setLocalValue({ date: value.date });

        analytics.event(
          "Date Select Click",
          {
            Date: value.date,
            Source: "WL",
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );
      }
      if (value.startTime) {
        setLocalValue({
          ...localValue,
          startTime: value.startTime,
          endTime: value.endTime,
        });
        if (value.endTime) {
          onComplete({
            date: isHourlyValue(localValue) ? localValue.date : undefined,
            startTime: value.startTime,
            endTime: value.endTime,
          });
        }
      }
    },
    [onSelectDate, analytics, onComplete, localValue]
  );

  const handleDailyChange = useCallback(
    (value: DailyValue) => {
      if (value.startDate) {
        onSelectDate(parseISO(value.startDate));

        analytics.event(
          "Date Select Click",
          {
            Date: value.startDate,
            Source: "WL",
          },
          { onlyProviders: ["mixpanel", "customerio"] }
        );
      }

      setLocalValue({ startDate: value.startDate });

      if (value.endDate) {
        onComplete({
          startDate: value.startDate,
          endDate: value.endDate,
        });
      }
    },
    [onSelectDate, analytics, onComplete]
  );

  const handleSave = () => {
    setShow(false);
    onSave?.();

    analytics.event(
      "Save Date Click",
      {
        "Booking Start Date": isHourlyValue(localValue)
          ? localValue?.date
          : localValue?.startDate,
        "Booking End Date": isHourlyValue(localValue)
          ? localValue?.date
          : localValue?.endDate,
        "Booking Start Time": isHourlyValue(localValue)
          ? localValue?.startTime
          : undefined,
        "Booking End Time": isHourlyValue(localValue)
          ? localValue?.endTime
          : undefined,
        Source: "WL",
      },
      { onlyProviders: ["mixpanel", "customerio"] }
    );
  };

  const handleSwitch = (value: BookingType) => {
    setSwitchValue(value);
    if (isHourlyValue(localValue)) {
      onComplete({ date: undefined });
    } else {
      onComplete({ startDate: undefined, endDate: undefined });
    }
  };

  const baseProps = {
    switchValue,
    space,
    timeslots,
    availableDays,
    onChangeMonth: onNextVisibleDates,
    onSwitch: handleSwitch,
    hideSwitch:
      !(hasDailyPrice && hasHourlyPrice) || !meetingRoomDailPricingFeatureFlag,
  };

  const calendarComponent: JSX.Element =
    switchValue === BookingType.Daily ? (
      <DateTimePicker<DailyValue>
        {...baseProps}
        value={!isHourlyValue(localValue) ? localValue : undefined}
        onChange={handleDailyChange}
        pickingRange={pickingRange}
      />
    ) : (
      <DateTimePicker<HourlyValue>
        {...baseProps}
        value={isHourlyValue(localValue) ? localValue : undefined}
        onChange={handleHourlyChange}
        pickingRange={PickingRange.OneDate}
      />
    );

  const hourlySelectedDateString =
    isHourlyValue(value) &&
    value.date &&
    formatMeetingRoomDateRange(value, fbt, locale, "");

  const dailySelectedDateString =
    !isHourlyValue(localValue) &&
    localValue?.startDate &&
    localValue.endDate &&
    formatDayRange(
      {
        start: localValue.startDate,
        end: localValue.endDate,
      },
      fbt,
      locale,
      "",
      localAvailableDates
        ?.filter((d) => d.availableFullDay)
        .map((d) => new Date(d.date))
    );

  const selectedDateString =
    hourlySelectedDateString || dailySelectedDateString;

  const isSaveButtonDisabled = isHourlyValue(value)
    ? !(value?.date && value.startTime && value.endTime)
    : !(value?.startDate && value.endDate);

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        className={`checkout-box-picker row ${disabled ? "disabled" : ""}`}
        id={`checkout-datetime-picker`}
        show={show}
        autoClose="outside"
        onToggle={(isOpen) => !disabled && setShow(isOpen)}
      >
        <Dropdown.Toggle data-testid="date-selection">
          <div className="row">
            <div className="col-10 checkout-box-picker-placeholder">
              <span className="row fw-bold text-uppercase checkout-box-picker-placeholder__label">
                {placeholder ? fbt(placeholder) : fbt("Date")}
              </span>
              <span className="row checkout-box-picker-selected">
                {selectedDateString ? (
                  selectedDateString
                ) : (
                  <span className="fw-lighter checkout-box-picker-selected__placeholder">
                    {fbt("Select date")}
                  </span>
                )}
              </span>
            </div>
            <div className="checkout-box-picker-icon">
              <Icon name="fi-chevron-down" size={24} />
            </div>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              {
                name: "preventOverflow",
                options:
                  device !== "mobile"
                    ? {
                        altAxis: true,
                        padding: { top: 80 },
                      }
                    : undefined,
              },
            ],
          }}
          className="checkout-datetime-picker rounded"
          style={{ minWidth: 350 }}
        >
          <div data-testid="daily-hourly-calendar">
            {calendarComponent}
            <div className="px-3 pb-3 d-flex flex-row-reverse justify-content-between align-items-center">
              <button
                disabled={isSaveButtonDisabled}
                className="btn btn-primary"
                onClick={handleSave}
                data-testid="daily-hourly-save-button-testId"
              >
                {fbt("Save")}
              </button>
              {durationPickerFeatureFlag && selectedDateString && (
                <div>{selectedDateString}</div>
              )}
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

interface TimeRangePickerProps {
  timeslots: TimeSlot[];
  value?: Partial<TimeInterval> | null;
  onChangeTime?: (value: Partial<TimeInterval>) => void;
}

export function TimeRangePicker(props: TimeRangePickerProps) {
  const { timeslots, value, onChangeTime } = props;
  const { start, end } = value || {};
  const { fbt } = useI18n();

  const handleStartTimeChange = React.useCallback(
    (time: Time) => {
      if (!onChangeTime) {
        return;
      }

      onChangeTime({
        start: time,
      });
    },
    [onChangeTime]
  );

  const handleEndTimeChange = React.useCallback(
    (time: Time) => {
      if (!onChangeTime) {
        return;
      }

      onChangeTime({
        start,
        end: time,
      });
    },
    [start, onChangeTime]
  );

  const startTimeOptions = timeslots.map((t) => ({
    value: t.startTime,
    label: fbt.formatTime(parseTime(t.startTime)),
    hasBetterRate: t.hasBetterRate,
    hasHigherRate: t.hasHigherRate,
  }));
  const startTimeSlots = timeslots.find((t) => t.startTime === start);

  const endTimeOptions = startTimeSlots
    ? startTimeSlots.endTime.map((t) => ({
        value: t,
        label: fbt.formatTime(parseTime(t)),
      }))
    : [];

  return (
    <>
      <div className="time-range-picker-start">
        <TimePicker
          label={fbt("Start time")}
          onChange={handleStartTimeChange}
          options={startTimeOptions}
          value={start}
          disabled={timeslots?.length < 1}
        />
      </div>
      <div className="time-range-picker-to">
        <span>{fbt("to")}</span>
      </div>
      <div className="time-range-picker-end">
        <TimePicker
          label={fbt("End time")}
          onChange={handleEndTimeChange}
          options={endTimeOptions}
          value={end}
          disabled={!start}
        />
      </div>
    </>
  );
}

interface TimePickerOption {
  label: string;
  value: string;
  hasBetterRate?: boolean;
  hasHigherRate?: boolean;
}

interface TimePickerProps {
  label: string;
  options: TimePickerOption[];
  value?: string;
  onChange: (value: string) => void;
  disabled: boolean;
}

export function TimePicker(props: TimePickerProps) {
  const { label, value, options, onChange, disabled = false } = props;
  const { fbt } = useI18n();

  return (
    <div className="checkout-box-picker-view">
      <Dropdown
        className="checkout-box-picker time-picker"
        id={`checkout-time-picker`}
        data-testid={"checkout-time-picker-testId"}
        drop="down"
        onSelect={(value: any) => onChange(value)}
      >
        <Dropdown.Toggle disabled={disabled}>
          <div className="row">
            <div className="col-10">
              {value ? (
                <span className="checkout-box-picker-selected">
                  {fbt.formatTime(parseTime(value))}
                </span>
              ) : (
                <span>{label}</span>
              )}
            </div>
            <div className="checkout-box-datetime-picker-icon">
              <Icon name="fi-chevron-down" size={24} />
            </div>
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="time-picker-v2"
          data-testid="checkout-time-picker-dropdown-menu"
        >
          {options.map((opt) => (
            <Dropdown.Item
              className="time-picker-v2"
              eventKey={opt.value}
              data-testid={"time-picker-v2-testId"}
              active={opt.value === value}
            >
              <span
                className={classNames(
                  "col align-self-start",
                  opt.hasBetterRate
                    ? "better-hour"
                    : opt.hasHigherRate
                    ? "higher-hour"
                    : ""
                )}
              >
                {opt.label}
                {opt.hasBetterRate && (
                  <Icon name="dot" size={6} color="#038231" />
                )}
                {opt.hasHigherRate && (
                  <Icon name="dot" size={6} color="#FF9E18" />
                )}
              </span>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}
